/**
 * --------------------------------------------------------------------------
 * Liander Bootstrap nav-main.js
 * --------------------------------------------------------------------------
 */

import { defineJQueryPlugin, isVisible } from '../../node_modules/bootstrap/js/src/util/index.js'
import EventHandler from '../../node_modules/bootstrap/js/src/dom/event-handler.js'
import SelectorEngine from '../../node_modules/bootstrap/js/src/dom/selector-engine.js'
import BaseComponent from '../../node_modules/bootstrap/js/src/base-component.js'

/**
 * Constants
 */

const NAME = 'nav-main'
const DATA_KEY = 'lbs.nav-modal'
const EVENT_KEY = `.${DATA_KEY}`
const DATA_API_KEY = '.data-api'
const EVENT_LOAD_DATA_API = `load${EVENT_KEY}${DATA_API_KEY}`

const CLASS_NAME_OPEN = 'open'
const CLASS_NAME_STICKY = 'sticky'

const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`
const EVENT_SCROLL_DATA_API = `scroll${EVENT_KEY}${DATA_API_KEY}`

const SELECTOR_MENU = '.nav-main'
const SELECTOR_MENU_MOBILE = '.nav-main-mobile'
const SELECTOR_PRIMARY_ITEM = '.nav-main-primary-item'
const SELECTOR_PRIMARY_CONTAINER_ACTIVE = '.nav-main-secondary-item.active .nav-main-primary-container'
const SELECTOR_PRIMARY_BUTTON = '.nav-main-primary-link'
const SELECTOR_PRIMARY_ICON = '.nav-main-primary-link-icon'
const SELECTOR_DATA_TOGGLE_MOBILE = '[data-lbs-toggle="nav-main-mobile"]'

const SECONDARY_MENU_HEIGHT = 40
const PRIMARY_MENU_MARGIN_TOP = 12
const PRIMARY_MENU_OFFSET_TOP = SECONDARY_MENU_HEIGHT + PRIMARY_MENU_MARGIN_TOP

const Default = {
  iconMenuClosed: 'fa-angle-down',
  iconMenuOpen: 'fa-angle-up',
  mobileIsOpen: false
}

const DefaultType = {
  iconMenuClosed: 'string',
  iconMenuOpen: 'string',
  mobileIsOpen: 'boolean'
}

class NavMain extends BaseComponent {
  constructor(element, config) {
    super(element, config)

    this._primaryMenuOffsetTop = PRIMARY_MENU_OFFSET_TOP

    this.checkPrimaryMenu()
    this._initMobileMenu()
  }

  // Getters
  static get Default() {
    return Default
  }

  static get DefaultType() {
    return DefaultType
  }

  static get NAME() {
    return NAME
  }

  // Public
  checkPrimaryMenu() {
    if (!this._inMobileMode()) {
      const menu = SelectorEngine.findOne(SELECTOR_PRIMARY_CONTAINER_ACTIVE)
      if (menu) {
        if (!this._isSticky(menu)) {
          this._initPrimaryMenuOffsetTop(menu)
        }

        this._setPrimaryMenuSticky(menu)
      }
    }
  }

  toggleMobileMenu() {
    if (this._config.mobileIsOpen) {
      this._hideMobileMenu()
    } else {
      this._showMobileMenu()
    }

    this._config.mobileIsOpen = !this._config.mobileIsOpen
  }

  toggleSubMenu(relatedTarget) {
    let isOpen = false
    const parents = SelectorEngine.parents(relatedTarget, SELECTOR_PRIMARY_ITEM)
    for (const parent of parents) {
      isOpen = parent.classList.contains(CLASS_NAME_OPEN)
    }

    this._closeSubMenus()

    for (const parent of parents) {
      if (isOpen) {
        this._hideSubMenu(parent)
      } else {
        this._showSubMenu(parent)
      }
    }
  }

  // Private
  _closeSubMenus() {
    const parents = SelectorEngine.find(SELECTOR_PRIMARY_ITEM)
    for (const parent of parents) {
      this._hideSubMenu(parent)
    }
  }

  _getMobileMenu() {
    return SelectorEngine.findOne(SELECTOR_MENU_MOBILE)
  }

  _hideMobileMenu() {
    this._element.classList.remove(CLASS_NAME_OPEN)
    const element = SelectorEngine.findOne(SELECTOR_MENU_MOBILE)
    element.classList.remove(CLASS_NAME_OPEN)
  }

  _hideSubMenu(parent) {
    parent.classList.remove(CLASS_NAME_OPEN)

    const button = SelectorEngine.findOne(SELECTOR_PRIMARY_BUTTON, parent)
    const subMenuToggleIcon = SelectorEngine.findOne(SELECTOR_PRIMARY_ICON, button)
    if (subMenuToggleIcon !== null) {
      subMenuToggleIcon.classList.add(this._config.iconMenuClosed)
      subMenuToggleIcon.classList.remove(this._config.iconMenuOpen)
    }
  }

  _initMobileMenu() {
    if (this._config.mobileIsOpen) {
      this._showMobileMenu()
    } else {
      this._hideMobileMenu()
    }
  }

  _initPrimaryMenuOffsetTop(menu) {
    const viewportOffset = menu.getBoundingClientRect()
    this._primaryMenuOffsetTop = viewportOffset.top + window.scrollY - PRIMARY_MENU_MARGIN_TOP
  }

  _inMobileMode() {
    return isVisible(this._getMobileMenu())
  }

  _isSticky(element) {
    return element.classList.contains(CLASS_NAME_STICKY)
  }

  _showMobileMenu() {
    this._element.classList.add(CLASS_NAME_OPEN)
    const element = SelectorEngine.findOne(SELECTOR_MENU_MOBILE)
    element.classList.add(CLASS_NAME_OPEN)
  }

  _showSubMenu(parent) {
    parent.classList.add(CLASS_NAME_OPEN)

    const button = SelectorEngine.findOne(SELECTOR_PRIMARY_BUTTON, parent)
    const subMenuToggleIcon = SelectorEngine.findOne(SELECTOR_PRIMARY_ICON, button)
    if (subMenuToggleIcon) {
      subMenuToggleIcon.classList.remove(this._config.iconMenuClosed)
      subMenuToggleIcon.classList.add(this._config.iconMenuOpen)
    }
  }

  _setPrimaryMenuSticky(menu) {
    if (window.scrollY > this._primaryMenuOffsetTop) {
      menu.classList.add(CLASS_NAME_STICKY)
    } else {
      menu.classList.remove(CLASS_NAME_STICKY)
    }
  }

  // Static
  static jQueryInterface(config) {
    return this.each(function () {
      const data = NavMain.getOrCreateInstance(this, config)

      if (typeof config !== 'string') {
        return
      }

      if (data[config] === undefined || config.startsWith('_') || config === 'constructor') {
        throw new TypeError(`No method named "${config}"`)
      }

      data[config](this)
    })
  }
}

/**
 * Data API implementation
 */

EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE_MOBILE, () => {
  const element = SelectorEngine.findOne(SELECTOR_MENU)
  if (element) {
    const data = NavMain.getOrCreateInstance(element)
    data.toggleMobileMenu()
  }
})

EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_PRIMARY_BUTTON, function () {
  const element = SelectorEngine.findOne(SELECTOR_MENU)
  if (element) {
    const data = NavMain.getOrCreateInstance(element)

    if (['BUTTON'].includes(this.tagName)) {
      data.toggleSubMenu(this)
    }
  }
})

EventHandler.on(window, EVENT_LOAD_DATA_API, () => {
  const element = SelectorEngine.findOne(SELECTOR_MENU)
  if (element !== null) {
    NavMain.getOrCreateInstance(element)
  }
})

EventHandler.on(window, EVENT_SCROLL_DATA_API, () => {
  const element = SelectorEngine.findOne(SELECTOR_MENU)
  if (element) {
    const data = NavMain.getOrCreateInstance(element)
    data.checkPrimaryMenu()
  }
})

/**
 * jQuery
 */

defineJQueryPlugin(NavMain)

export default NavMain
